import { iconEnumToString } from "@storytell/bsky-icons";
import {
  AssociateLabelRequestConfig,
  CreateLabelRequestConfig,
  DisassociateLabelRequestConfig,
  useStorytellClient,
} from "@storytell/client";
import type { Icons } from "@storytell/cp-client";
import type { AssetId, Label, LabelAssociation, LabelId } from "@storytell/types";
import { type Component, createEffect, createMemo, createSignal } from "solid-js";
import type { LabelsService } from "../../stores/labels/labelsService";
import type { RelsService } from "../../stores/rels/relsService";
import { LabelPicker, type LabelPickerStrings } from "./LabelPicker";
import { filterLabelsForScope, type LabelListScope, shouldAutoExpandScopeToAll } from "./labelScopeFilter";

interface AssetLabelPickerProps {
  assetId: string;
  assetLabels: Label[];
  onLabelCreated: (label: Label) => void;
  mount?: Node;
  onClose?: () => void;
  onOpenCreateModal?: (initialName?: string) => void;
  // Service dependencies
  labelsService: LabelsService;
  relsService: RelsService;
  authToken: () => string | undefined;
  canCreateLabels?: boolean;
  strings?: LabelPickerStrings;
}

export const AssetLabelPicker: Component<AssetLabelPickerProps> = (props) => {
  const [search, setSearch] = createSignal("");
  const [labelScope, setLabelScope] = createSignal<LabelListScope>("my");
  const storytellClient = useStorytellClient();

  /** Single search per keystroke; memo is shared by filtered list + scope auto-expand effect. */
  const searchedLabels = createMemo(() => props.labelsService.search(search()));

  const labels = createMemo(() => filterLabelsForScope(searchedLabels(), labelScope()));

  createEffect(() => {
    const q = search().trim();
    if (shouldAutoExpandScopeToAll(searchedLabels(), labelScope(), q)) {
      setLabelScope("all");
    }
  });

  const onCreateLabel = async (data: { name: string; description: string; icon: Icons.Icon; color: string }) => {
    try {
      const client = useStorytellClient();
      const request = CreateLabelRequestConfig.withBearerToken(props.authToken());
      const result = await client.fetch(request, undefined, {
        name: data.name,
        description: data.description,
        icon: iconEnumToString(data.icon),
        color: data.color,
      });

      if (result.isOk()) {
        const label = result.value;
        props.labelsService.set(label.id, { ...label });
        await onAddLabelToAsset(label.id);
        props.onLabelCreated(label);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onAddLabelToAsset = async (labelId: LabelId) => {
    props.relsService.addLabelToAsset(props.assetId as AssetId, labelId);

    try {
      const requestConfig = AssociateLabelRequestConfig.withBearerToken(props.authToken());
      const result = await storytellClient.fetch(requestConfig, undefined, [
        { labelId, resourceId: props.assetId, resourceType: "asset" },
      ]);

      if (result.isErr()) {
        props.relsService.removeLabelFromAsset(props.assetId as AssetId, labelId);
        return;
      }

      result.value.forEach((assoc) => props.labelsService.onLabelAssociated(assoc as LabelAssociation));
    } catch (error) {
      console.error(error);
      props.relsService.removeLabelFromAsset(props.assetId as AssetId, labelId);
    }
  };

  const onRemoveLabelFromAsset = async (labelId: LabelId) => {
    const existing = props.relsService.assetHasLabel(props.assetId as AssetId, labelId);
    if (!existing) return;

    props.relsService.removeLabelFromAsset(props.assetId as AssetId, labelId);
    try {
      const requestConfig = DisassociateLabelRequestConfig.withBearerToken(props.authToken());
      const associationId = props.labelsService.getAssociation(labelId, props.assetId)?.id;
      if (!associationId) return;
      const result = await storytellClient.fetch(requestConfig, undefined, [
        {
          associationId,
        },
      ]);

      if (result.isErr()) {
        props.relsService.addLabelToAsset(props.assetId as AssetId, labelId);
        return;
      }

      props.labelsService.onLabelDisassociated(associationId);
    } catch (error) {
      console.error(error);
      props.relsService.addLabelToAsset(props.assetId as AssetId, labelId);
    }
  };

  return (
    <LabelPicker
      search={search()}
      setSearch={setSearch}
      labels={labels()}
      selectedLabels={props.assetLabels.map((l) => l.id)}
      onSelectLabel={(label) => void onAddLabelToAsset(label.id)}
      onDeselectLabel={(label) => void onRemoveLabelFromAsset(label.id)}
      onCreateLabel={onCreateLabel}
      mount={props.mount}
      onClose={props.onClose}
      onOpenCreateModal={() => {
        // Close the parent popover when opening the create label modal
        props.onClose?.();
        // Call the parent's handler to open the modal with the search text
        props.onOpenCreateModal?.(search());
      }}
      canCreateLabels={props.canCreateLabels}
      strings={props.strings}
      showLabelScopeToggle
      labelScope={labelScope()}
      onLabelScopeChange={setLabelScope}
    />
  );
};
